/* eslint-disable no-undef */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

/**
 * Colour-coded FHRS hygiene rating badge with detail popup.
 * Renders nothing if the lead has no fhrs_id, no rating, or is Exempt.
 */

const RATING_COLOURS = {
  '5': { bg: 'bg-green-500', text: 'text-green-800', border: 'border-green-300', label: 'Very Good' },
  '4': { bg: 'bg-lime-400', text: 'text-lime-800', border: 'border-lime-300', label: 'Good' },
  '3': { bg: 'bg-amber-400', text: 'text-amber-800', border: 'border-amber-300', label: 'Generally Satisfactory' },
  '2': { bg: 'bg-orange-400', text: 'text-orange-800', border: 'border-orange-300', label: 'Improvement Necessary' },
  '1': { bg: 'bg-red-400', text: 'text-red-800', border: 'border-red-300', label: 'Major Improvement Necessary' },
  '0': { bg: 'bg-red-600', text: 'text-red-900', border: 'border-red-400', label: 'Urgent Improvement Necessary' },
};

const NEUTRAL_COLOUR = { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };

const HygieneRatingBadge = ({ lead }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupStyle, setPopupStyle] = useState(null);
  const popupRef = useRef(null);
  const badgeRef = useRef(null);

  // Position popup so it stays within the viewport
  const positionPopup = useCallback(() => {
    if (!badgeRef.current || !popupRef.current) return;
    const badgeRect = badgeRef.current.getBoundingClientRect();
    const popupHeight = popupRef.current.offsetHeight;
    const popupWidth = 288;

    let top = badgeRect.bottom + 4;
    let left = badgeRect.right - popupWidth;

    if (left + popupWidth > window.innerWidth - 8) left = window.innerWidth - popupWidth - 8;
    if (left < 8) left = 8;
    if (top + popupHeight > window.innerHeight - 8) top = badgeRect.top - popupHeight - 4;
    if (top < 8) top = 8;

    setPopupStyle({ position: 'fixed', top: `${top}px`, left: `${left}px`, visibility: 'visible' });
  }, []);

  // Once popup DOM is rendered (but invisible), measure and position it
  useEffect(() => {
    if (!showPopup) { setPopupStyle(null); return; }

    // Wait one frame for the popup to render so we can measure its height
    const raf = requestAnimationFrame(() => positionPopup());

    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          badgeRef.current && !badgeRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    const handleScroll = () => setShowPopup(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showPopup, positionPopup]);

  // Don't render anything if no FHRS data
  if (!lead.fhrs_id) return null;

  const rating = lead.fhrs_rating;
  const displayValue = rating !== null && rating !== undefined ? String(rating) : '?';
  const isNumeric = /^[0-5]$/.test(displayValue);
  const isExempt = /exempt/i.test(displayValue);
  const isAwaiting = /await/i.test(displayValue);
  const isPass = /pass/i.test(displayValue);
  const isImprovement = /improvement/i.test(displayValue) && !isPass;

  // Don't show Exempt at all
  if (isExempt) return null;

  const colours = isNumeric ? RATING_COLOURS[displayValue] : NEUTRAL_COLOUR;

  let badgeColours = colours;
  if (isPass) badgeColours = RATING_COLOURS['5'];
  else if (isAwaiting) badgeColours = NEUTRAL_COLOUR;
  else if (isImprovement) badgeColours = RATING_COLOURS['1'];

  // Short label for badge
  let badgeLabel = displayValue;
  if (!isNumeric) {
    if (isPass) badgeLabel = 'Pass';
    else if (isAwaiting) badgeLabel = 'Awaiting';
    else if (isImprovement) badgeLabel = 'Imp';
  }

  // Build full FHRS address string
  const fhrsAddress = [lead.fhrs_address, lead.fhrs_address2, lead.fhrs_address3, lead.fhrs_postcode]
    .filter(Boolean)
    .join(', ');

  // Readable display value for popup (add spaces to camelCase like "AwaitingInspection")
  const popupDisplayValue = displayValue.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Inspection date — skip bogus dates (1901, 1970 etc)
  let inspectDate = null;
  if (lead.fhrs_rating_date) {
    const d = new Date(lead.fhrs_rating_date);
    if (d.getFullYear() > 2000) {
      inspectDate = d.toLocaleDateString('en-GB');
    }
  }

  return (
    <>
      <div className="flex flex-col items-center">
        <button
          ref={badgeRef}
          onClick={() => setShowPopup(!showPopup)}
          className={isNumeric
            ? `inline-flex items-center justify-center w-9 h-9 rounded-full text-base font-extrabold border-2 border-gray-900 cursor-pointer hover:opacity-80 transition-opacity ${badgeColours.bg} text-gray-900`
            : `inline-flex items-center px-2 py-0.5 rounded text-sm font-extrabold border-2 border-gray-900 cursor-pointer hover:opacity-80 transition-opacity ${badgeColours.bg} text-gray-900`
          }
          title="Click for hygiene details"
        >
          {isAwaiting ? (
            <span className="text-center leading-tight" style={{ fontSize: '0.6rem' }}>Awaiting<br/>Inspection</span>
          ) : (
            <>{badgeLabel}{lead.fhrs_new_rating_pending ? '*' : ''}</>
          )}
        </button>
        {inspectDate && <div className="text-gray-500 mt-0.5" style={{ fontSize: '0.65rem' }}>{inspectDate}</div>}
      </div>

      {lead.fhrs_match_confidence === 'medium' && (
        <span className="ml-1 text-gray-400 text-xs" title="Medium confidence match (proximity)">~</span>
      )}

      {showPopup && (
        <div
          ref={popupRef}
          style={{ width: 288, ...(popupStyle || { position: 'fixed', top: 0, left: '-9999px', visibility: 'hidden' }) }}
          className="z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-xs whitespace-normal"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-gray-800">Food Hygiene Rating</span>
            <button onClick={() => setShowPopup(false)} className="text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          </div>

          {/* Rating display */}
          <div className="flex items-center gap-3 mb-2">
            {isNumeric ? (
              <div className="flex flex-col items-center shrink-0">
                <div className={`inline-flex items-center justify-center w-11 h-11 rounded-full text-lg font-extrabold border-2 border-gray-900 ${badgeColours.bg} text-gray-900`}>
                  {displayValue}
                </div>
                {inspectDate && <div className="text-gray-500 mt-0.5" style={{ fontSize: '0.65rem' }}>{inspectDate}</div>}
              </div>
            ) : (
              <span className={`text-lg font-bold ${badgeColours.text}`}>{popupDisplayValue}</span>
            )}
            {isNumeric && <span className="text-sm font-medium text-gray-700">{RATING_COLOURS[displayValue]?.label}</span>}
          </div>

          {/* Pending indicator */}
          {lead.fhrs_new_rating_pending ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mb-2 text-yellow-700">
              New rating pending — recently inspected
            </div>
          ) : null}

          {/* Details */}
          <div className="space-y-1 text-gray-600" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            {lead.fhrs_business_name && (
              <div><span className="text-gray-400">FHRS Name:</span> {lead.fhrs_business_name}</div>
            )}
            {lead.fhrs_business_type && (
              <div><span className="text-gray-400">Type:</span> {lead.fhrs_business_type}</div>
            )}
            {inspectDate && !isNumeric && (
              <div><span className="text-gray-400">Inspected:</span> {inspectDate}</div>
            )}
            <div><span className="text-gray-400">Address:</span> {fhrsAddress || 'Not available'}</div>
            <div>
              <span className="text-gray-400">Match:</span>{' '}
              <span className={lead.fhrs_match_confidence === 'high' ? 'text-green-600' : 'text-amber-600'}>
                {lead.fhrs_match_confidence === 'high' ? 'High confidence' : 'Medium confidence (proximity)'}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HygieneRatingBadge;
